import React from 'react'
import { Box, SxProps, Typography, type TypographyProps } from '@mui/material'
import { Link } from '@assets-components'
import { Icon, Image, type ImageData } from '@media-components'
import { ActionButton } from '@components/common/ActionButton'
import { SimpleDocumentRenderer } from '@components/common/DocumentRenderer'
import { documentCleaner } from '@utils/function'
import { BaselineStyleClasses, CustomStyles, getStyles } from '@utils/styles'
import { IconImage, IconImageProps } from '../../IconImage'
import { CardData } from '../DefaultCard'
import defaultStyles, { StylesClassNames } from './styles'

export type FeatureCardData = CardData

export type FeatureCardProps = CardData & {
  reverseLayout?: boolean
  dark?: boolean
  csx?: CustomStyles<StylesClassNames>
  sx?: SxProps
  animation?: IconImageProps['animation']
  iconImageSx?: SxProps
  imageData?: ImageData
  titleVariant?: TypographyProps['variant']
}

// eslint-disable-next-line complexity
export function FeatureCard({
  description,
  iconData,
  reverseLayout = false,
  title,
  csx,
  sx,
  dark = false,
  actionButtonData,
  animation,
  points,
  documentData,
  iconImageSx,
  imageData,
  href,
  titleVariant = 'body1.500',
}: FeatureCardProps) {
  const styles = getStyles(defaultStyles, csx)

  const cardHref = href
  const isLinkProvided = Boolean(cardHref)
  const cleanDocument = documentCleaner(documentData?.document ?? [])

  return (
    <Box
      {...styles('wrapper', {
        flexDirection: reverseLayout ? 'row-reverse' : 'row',
        backgroundColor: dark ? 'custom.lightGrey' : 'background.paper',
        transition: 'box-shadow 0.2s ease-in-out',
        ...(!imageData &&
          isLinkProvided && {
            '&:hover': {
              boxShadow: '0px 8px 28px rgba(0, 0, 0, 0.18)',
            },
          }),
        ...sx,
      })}
      component={isLinkProvided ? Link : 'div'}
      {...(isLinkProvided && {
        href: cardHref,
        'aria-label': `${title} - ${description || ''}`,
      })}
      className={BaselineStyleClasses.imageTransitioningWrapper}
    >
      <Box {...styles('contentContainer')}>
        {iconData ? (
          <IconImage
            {...iconData}
            animation={animation}
            size="medium"
            styles={iconImageSx}
            alt={`Icon representing ${title}`}
            darkBg={dark}
          />
        ) : (
          !imageData && (
            <Box {...styles('defaultCheckIcon')} aria-hidden="true">
              <Icon iconName="Tick" colorValues="green.600" fontSize="small" />
            </Box>
          )
        )}

        {/* {!!chipsData?.length && (
          <Box {...styles('tagWrapper')} role="group" aria-label="Feature tags">
            {chipsData.map((item, idx) => (
              <Chip
                key={idx}
                {...item}
                selected
                noIcon
                // FIXME: pass styles to wrapper via sx
                csx={{
                  wrapper: styles('tag', {
                    outlineColor: dark ? 'border.contrastText' : 'border.light',
                    backgroundColor: dark
                      ? 'background.paperDark'
                      : 'background.paper',
                    color: dark ? 'text.primaryDark' : 'text.primary',
                  }).sx,
                }}
              />
            ))}
          </Box>
        )} */}
        {imageData && (
          <Image
            {...imageData}
            {...styles('image')}
            alt={`Image representing ${title}`}
            className={BaselineStyleClasses.imageTransition}
          />
        )}
        <Box {...styles('infoWrapper')}>
          {title.trim() && (
            <Typography
              variant={titleVariant}
              component={
                description ||
                !!documentData?.document.length ||
                !!points?.length
                  ? 'h3'
                  : 'span'
              }
              {...(styles('title'),
              {
                color: dark ? 'text.primaryDark' : 'text.primary',
              })}
            >
              {title}
            </Typography>
          )}

          {description && (
            <Typography
              variant="body3"
              {...styles('description', {
                color: dark ? 'text.secondaryDark' : 'text.secondary',
              })}
            >
              {description}
            </Typography>
          )}

          {cleanDocument.length > 0 && (
            <SimpleDocumentRenderer
              {...styles('documentWrapper', {
                '& h1, h2, h3, h4, h5, h6': {
                  color: dark ? 'text.primaryDark' : 'text.primary',
                  margin: '0px !important',
                },
                '& p, li, span': {
                  color: dark ? 'text.secondaryDark' : 'text.secondary',
                },
              })}
              document={cleanDocument}
            />
          )}

          {!!points?.length && (
            <Box
              component="ul"
              {...styles('ul')}
              role="list"
              aria-label={`Key points for ${title}`}
            >
              {points.map((item, index) => (
                <Typography
                  key={index}
                  component="li"
                  variant="body2.400"
                  color="grey.500"
                  {...styles('li')}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          )}
        </Box>

        {actionButtonData && actionButtonData.children && (
          // <Button
          //   variant="text"
          //   {...styles(['button', dark ? 'darkButton' : 'lightButton'])}
          //   className="feature-card-cta"
          //   endIcon={
          //     <Icon
          //       iconName="ArrowRight"
          //       colorValues={dark ? 'common.white' : 'grey.600'}
          //       sx={{ zIndex: 2, zoom: '0.8' }}
          //       className="arrowIcon"
          //       fontSize="extraSmall"
          //       aria-hidden="true"
          //     />
          //   }
          //   href={actionButtonData.href}
          //   aria-label={`${actionButtonData.children} for ${title}`}
          // >
          //   {actionButtonData.children}
          // </Button>
          <ActionButton
            {...actionButtonData}
            variant="text"
            sx={{
              padding: '12px 4px !important',
              ':hover': {
                backgroundColor: 'transparent !important',
                textDecoration: 'underline !important',
              },
            }}
            endIcon={
              <Icon
                iconName="ArrowRight"
                colorValues={dark ? 'common.white' : 'grey.600'}
                sx={{ zIndex: 2, zoom: '0.8' }}
                className="arrowIcon"
                fontSize="extraSmall"
                aria-hidden="true"
              />
            }
          />
        )}
      </Box>
    </Box>
  )
}
